import React, { useEffect, useRef, useState } from 'react';
import { MenuIcon, SearchIcon, PhoneIcon, MapPinIcon, UserIcon, XIcon } from 'lucide-react';
import { advisor, dealership } from '../data/catalog';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from './LanguageToggle';
const navItems = [{
  key: 'new',
  href: '#inventario'
}, {
  key: 'preOwned',
  href: '#categorias'
}, {
  key: 'service',
  href: '#servicio'
}, {
  key: 'finance',
  href: '#financiamiento'
}, {
  key: 'contact',
  href: '#contacto'
}] as const;
export function Header() {
  const {
    t
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setMenuOpen(false);
      setSearchOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <header className="fixed inset-x-0 top-0 z-50" data-id="element-116" data-mp="35lj1zt3z3rla">
      {/* Barra superior — solo desktop, se retrae al hacer scroll */}
      <div className={`hidden overflow-hidden border-b border-line bg-canvas transition-[max-height,opacity] duration-500 ease-[var(--ease-lux)] md:block ${scrolled ? 'max-h-0 border-b-0 opacity-0' : 'max-h-12 opacity-100'}`} data-id="element-117" data-mp="30n7u3nx8nr5p">
        <div className="mx-auto flex max-w-[1520px] items-center justify-end gap-8 px-8 py-2.5 lg:px-14" data-id="element-118" data-mp="3t9adwvllq2m1">
          <a href="#contacto" className="link-underline flex items-center gap-2 text-[11px] tracking-wide text-mute transition-colors duration-500 ease-[var(--ease-lux)] hover:text-white" data-id="element-119" data-mp="1wco77x3ehg5d">
            <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-120" data-mp="1mm30tnhhj7bw" />
            {dealership.address}
          </a>
          <a href={advisor.emailHref} className="link-underline text-[11px] tracking-wide text-mute transition-colors duration-500 ease-[var(--ease-lux)] hover:text-white" data-id="element-121" data-mp="18m0k44kwyjy8">
            {advisor.email}
          </a>
        </div>
      </div>

      {/* Navegación principal */}
      <div className={`border-b bg-canvas transition-colors duration-500 ease-[var(--ease-lux)] ${scrolled ? 'border-line' : 'border-transparent'}`} data-id="element-122" data-mp="13os3i8feynz3">
        <div className="mx-auto flex h-14 max-w-[1520px] items-center gap-4 px-6 lg:h-16 lg:px-14" data-id="element-123" data-mp="2y0yrk7teoz8t">
          <button type="button" onClick={() => setMenuOpen(true)} aria-label={t.header.openMenu} aria-expanded={menuOpen} className="-ml-2 p-2 text-white lg:hidden" data-id="element-124" data-mp="mfpt284e5sk1">
            <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-125" data-mp="w6ki1k90v696" />
          </button>

          <a href="#main" className="mr-auto flex items-center gap-3 text-white" data-id="element-126" data-mp="rc6e2tganqsw">
            <span className="text-[13px] font-medium uppercase tracking-[0.34em] sm:text-[14px]" data-id="element-127" data-mp="153urd02a4lbg">
              {/* Wordmark estático con un destello sutil cada 15 s */}
              <span className="logo-glint" data-id="element-128" data-mp="1f3vdsuvg3hjv">Porsche</span>
            </span>
            <span className="hidden h-3 w-px bg-line sm:block" aria-hidden="true" data-id="element-129" data-mp="1a4924pv9m9d6" />
            <span className="hidden text-[10px] uppercase tracking-[0.28em] text-mute sm:block" data-id="element-130" data-mp="1a260t9onwtt5">
              North Miami
            </span>
          </a>

          <nav aria-label="Main" className="hidden xl:block" data-id="element-131" data-mp="3bwni3je913qy">
            <ul className="flex items-center gap-8" data-id="element-132" data-mp="22ynmxqh8x3ge">
              {navItems.map(item => <li key={item.key} data-id="element-133" data-mp="27xrw4h46716q">
                  <a href={item.href} className="link-underline py-2 text-[13px] tracking-wide text-mute transition-colors duration-500 ease-[var(--ease-lux)] hover:text-white" data-id="element-134" data-mp="27wp5maarf0rc">
                    {t.nav[item.key]}
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="ml-auto flex items-center gap-1.5 sm:gap-2 xl:ml-10" data-id="element-135" data-mp="1p7a7ooqderr6">
            {/* Selector de idioma, siempre visible — también en móvil */}
            <LanguageToggle className="mr-0.5 sm:hidden" showIcon={false} data-id="element-136" data-mp="1t7ax14f0cnd0" />
            <LanguageToggle className="mr-1 hidden sm:flex" data-id="element-137" data-mp="1y0m518vk3a6i" />

            {/* Búsqueda de inventario */}
            <div className="hidden items-center sm:flex" data-id="element-138" data-mp="11gmsrji1f1v4">
              <div className={`overflow-hidden transition-[width,opacity] duration-500 ease-[var(--ease-lux)] ${searchOpen ? 'w-40 opacity-100 sm:w-60' : 'w-0 opacity-0'}`} data-id="element-139" data-mp="15j9p9xpnlayn">
                <input ref={searchInputRef} type="search" placeholder={t.header.searchPlaceholder} aria-label={t.header.searchPlaceholder} tabIndex={searchOpen ? 0 : -1} className="w-full border-b border-line bg-transparent px-1 pb-1 text-[13px] text-white placeholder:text-mute focus:border-white focus:outline-none" data-id="element-140" data-mp="t1yzxsf0pt2c" />
              </div>
              <button type="button" onClick={() => setSearchOpen(v => !v)} aria-label={searchOpen ? t.header.searchClose : t.header.searchOpen} aria-expanded={searchOpen} className="p-2 text-mute transition-colors duration-300 hover:text-white" data-id="element-141" data-mp="5atk3d92v0s1">
                {searchOpen ? <XIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-142" data-mp="3nj297ystfohb" /> : <SearchIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-143" data-mp="3dvgh3o20ntm2" />}
              </button>
            </div>

            <a href="#contacto" aria-label={t.header.directions} className="hidden p-2 text-mute transition-colors duration-300 hover:text-white lg:block" data-id="element-144" data-mp="33vpltk5mtakx">
              <MapPinIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-145" data-mp="f8rroy9f45x" />
            </a>

            <a href="#contacto" aria-label={t.header.account} className="hidden p-2 text-mute transition-colors duration-300 hover:text-white lg:block" data-id="element-146" data-mp="5dk36447dmq9">
              <UserIcon className="h-[18px] w-[18px]" aria-hidden="true" data-id="element-147" data-mp="3rrtou5gr0gnc" />
            </a>

            {/* Llamada directa */}
            <a href={advisor.phoneHref} className="btn-glint btn-glint-sheen ml-1 flex items-center gap-2 border border-guards bg-guards px-4 py-2 text-[12px] tracking-wide text-white transition-colors duration-500 ease-[var(--ease-lux)] hover:bg-guards-bright" data-id="element-148" data-mp="jbqqiqbj04bo">
              <PhoneIcon className="h-[15px] w-[15px]" aria-hidden="true" data-id="element-149" data-mp="23szxuc86l3ul" />
              <span className="hidden lg:inline" data-id="element-150" data-mp="2ijgwxoxcfhob">{advisor.phoneLabel}</span>
              <span className="sr-only lg:hidden" data-id="element-151" data-mp="2dkni9t6blkmk">
                {t.header.call} {advisor.name}
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Menú móvil */}
      <div id="mobile-menu" role="dialog" aria-modal="true" aria-label="Menu" className={`fixed inset-0 z-50 bg-canvas transition-opacity duration-500 ease-[var(--ease-lux)] xl:hidden ${menuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`} data-id="element-152" data-mp="1kx1hesoh8u3e">
        <div className="flex h-14 items-center justify-between px-6" data-id="element-153" data-mp="3a9d1pnxlilan">
          <span className="text-[13px] font-medium uppercase tracking-[0.34em]" data-id="element-154" data-mp="368jekwrz9ait">
            Porsche North Miami
          </span>
          <button type="button" onClick={() => setMenuOpen(false)} aria-label={t.header.closeMenu} className="-mr-2 p-2" data-id="element-155" data-mp="13oqo4y6b1tv3">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-156" data-mp="1djiouav6owu3" />
          </button>
        </div>
        <nav aria-label="Mobile" className="px-6 pt-6" data-id="element-157" data-mp="1icllwsz66of1">
          <LanguageToggle size="full" className="w-fit" data-id="element-158" data-mp="1mdnlxc0wv4i2" />
          <ul className="mt-8 divide-y divide-line border-y border-line" data-id="element-159" data-mp="1md5h3r06nhyj">
            {navItems.map(item => <li key={item.key} data-id="element-160" data-mp="1rel25j2d1ogx">
                <a href={item.href} onClick={() => setMenuOpen(false)} className="block py-6 text-2xl font-light tracking-[-0.02em]" data-id="element-161" data-mp="1wb1r2fhdw6v2">
                  {t.nav[item.key]}
                </a>
              </li>)}
          </ul>
          <div className="mt-12 flex flex-col gap-4 text-[13px] text-mute" data-id="element-162" data-mp="6vv92r08qycg">
            <a href={advisor.phoneHref} className="text-white" data-id="element-163" data-mp="2k6wrhk4f5x81">
              {advisor.phoneLabel}
            </a>
            <a href={advisor.emailHref} className="break-all text-white" data-id="element-164" data-mp="2ffod09o8flo0">
              {advisor.email}
            </a>
            <span data-id="element-165" data-mp="2bfnzcr8yjei0">{dealership.address}</span>
          </div>
        </nav>
      </div>
    </header>;
}